import { useState, useEffect, useRef } from "react";
import io from "socket.io-client";
import ResultsChart from "./ResultsChart/ResultsChart";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faQrcode,
  faX,
  faMicrophone,
  faFolderClosed,
} from "@fortawesome/free-solid-svg-icons";
import { QRCodeSVG } from "qrcode.react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  viewPresentation,
  viewPublicPresentation,
  startSession,
  endSession,
} from "../redux/features/presentations/presentationsSlice";
import { useAnimation } from "./AnimateTab/AnimationContext";
import { SlideCanvasRenderer } from "./SlideCanvasRenderer";
import SpeakerNav from "./SpeakerNav";
import SpeakerRegistration from "./SpeakerRegistration";

const Speaker = () => {
  const { uuid, sessionId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [socket, setSocket] = useState(null);
  const [slideData, setSlideData] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [openQRCode, setOpenQRCode] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [slideResults, setSlideResults] = useState({});
  const [showEndConfirmModal, setShowEndConfirmModal] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [emailError, setEmailError] = useState("");
  const [cityError, setCityError] = useState("");
  const [isStarting, setIsStarting] = useState(false);
  const [activeSlide, setActiveSlide] = useState(null);

  const { selectedPresentation, getPresentationLoading } = useSelector(
    (state) => state.presentation,
  );
  const { runGSAPPreview, setElements, setInteractions } = useAnimation();
  const runGSAPPreviewRef = useRef(runGSAPPreview);
  runGSAPPreviewRef.current = runGSAPPreview;

  useEffect(() => {
    if (uuid) {
      if (localStorage.getItem("OrviSlideToken")) {
        dispatch(viewPresentation(uuid));
      } else {
        dispatch(viewPublicPresentation(uuid));
      }
    }
  }, [uuid, dispatch]);

  useEffect(() => {
    if (selectedPresentation && selectedPresentation.slides) {
      const formattedSlides = selectedPresentation.slides
        .filter((slide) => slide.is_active !== false)
        .map((slide) => {
          const slideType = slide.type;

          let parsedAnimationData = null;

          if (slide.animation_data) {
            try {
              const parsed =
                typeof slide.animation_data === "string"
                  ? JSON.parse(slide.animation_data)
                  : slide.animation_data;

              parsedAnimationData = {
                ...parsed,
                elements: (parsed?.elements || []).filter(Boolean),
                interactions: (parsed?.interactions || []).filter(Boolean),
              };
            } catch (err) {
              console.error("Failed to parse animation data", err);
            }
          }

          // QUESTION SLIDES
          if (slideType === "question" || (slide.question && !slide.image)) {
            return {
              id: slide.id,
              type: slide.question.question_type,
              title: slide.title,
              question: slide.question.question_text,
              options: slide.question.choices || [],
              with_animation: slide.with_animation || false,
              animation_data: parsedAnimationData,
            };
          }

          // IMAGE SLIDES
          if ((slideType === "image" || slide.image) && slide.image) {
            let url = slide.image.image_url;

            if (url && !url.startsWith("http") && !url.startsWith("blob:")) {
              try {
                const apiUrl =
                  process.env.REACT_APP_API_URL || "http://localhost:49152";
                const origin = new URL(apiUrl).origin;

                url = `${origin}${url.startsWith("/") ? "" : "/"}${url}`;
              } catch (e) {
                console.error("Error parsing API URL:", e);

                url = `${
                  process.env.REACT_APP_API_URL || "http://localhost:49152"
                }${url}`;
              }
            }

            return {
              id: slide.id,
              type: "image",
              title: slide.image.title || slide.title,
              imageUrl: url,
              with_animation: slide.with_animation || false,
              animation_data: parsedAnimationData,
            };
          }

          // BLANK SLIDES
          if (slideType === "blank") {
            return {
              id: slide.id,
              type: "blank",
              title: slide.title,
              canvasBackgroundColor:
                parsedAnimationData?.canvasBackgroundColor || "#1a1a2e",
              with_animation: slide.with_animation || false,
              animation_data: parsedAnimationData,
            };
          }

          // FALLBACK
          return {
            id: slide.id,
            type: slide.type,
            title: slide.title,
            question: slide.question,
            options: slide.options,
            imageUrl:
              slide.imageUrl ||
              (slide.file
                ? `${
                    process.env.REACT_APP_API_URL || "http://localhost:5000"
                  }/slides/${slide.file}`
                : null),
            with_animation: slide.with_animation || false,
            animation_data: parsedAnimationData,
          };
        });

      setSlideData(formattedSlides);
    }
  }, [selectedPresentation]);

  useEffect(() => {
    const slide = slideData[currentSlide];
    if (!slide?.with_animation || !slide?.animation_data) return;

    // Every animated slide type (question / image / blank) runs through the
    // same canvas pipeline so animations (move/fade/scale/rotate) play natively.
    const elements = (slide.animation_data?.elements || []).filter(Boolean);
    const interactions = (slide.animation_data?.interactions || []).filter(
      Boolean,
    );

    setElements(elements);
    setInteractions(interactions);
    const timer = setTimeout(() => runGSAPPreviewRef.current(), 50);
    return () => clearTimeout(timer);
  }, [currentSlide, slideData]);

  useEffect(() => {
    const socketConnection = io(
      process.env.REACT_APP_SOCKET_URL || "http://localhost:5000",
    );
    setSocket(socketConnection);

    socketConnection.on("connect", () => {
      socketConnection.emit("joinPresentation", { uuid, sessionId });
    });

    socketConnection.on("slideResults", ({ slideId, results }) => {
      setSlideResults((prev) => ({
        ...prev,
        [slideId]: results,
      }));
    });

    socketConnection.on("slideChange", (slideIndex) => {
      setCurrentSlide(slideIndex);
    });

    socketConnection.on("allResults", (results) => {
      setSlideResults(results);
    });

    return () => socketConnection.disconnect();
  }, []);

  const handleNextSlide = () => {
    setCurrentSlide((prev) => {
      const nextSlide = Math.min(prev + 1, slideData.length - 1);
      socket?.emit("nextSlide", { uuid, sessionId, slide: nextSlide });
      return nextSlide;
    });
  };

  const handlePreviousSlide = () => {
    setCurrentSlide((prev) => {
      const prevSlide = Math.max(prev - 1, 0);
      socket?.emit("previousSlide", { uuid, sessionId, slide: prevSlide });
      return prevSlide;
    });
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowRight") handleNextSlide();
      if (event.key === "ArrowLeft") handlePreviousSlide();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [slideData, currentSlide]);

  const handleEndPresentation = () => {
    setShowEndConfirmModal(true);
  };

  const confirmEndPresentation = async () => {
    setIsEnding(true);
    try {
      socket?.emit("endPresentation", { uuid, sessionId });

      if (sessionId) {
        await dispatch(endSession(sessionId)).unwrap();
        navigate(`/sessions/${sessionId}/end`);
      } else {
        navigate("/end-presentation");
      }
    } catch (error) {
      console.error("Failed to end presentation:", error);
    } finally {
      setIsEnding(false);
      setShowEndConfirmModal(false);
    }
  };

  const getAttendeeUrl = () => {
    if (sessionId) {
      return `${window.location.origin}/${uuid}/sessions/${sessionId}/attendee`;
    }
    return `${window.location.origin}/${uuid}/attendee`;
  };

  const getCurrentSlideResults = () => slideResults[currentSlide] || null;

  const handleStartSession = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setEmailError("Please enter your email address");
      return;
    }
    if (!city.trim()) {
      setCityError("Please enter your city");
      return;
    }

    setIsStarting(true);
    try {
      if (!selectedPresentation?.id) {
        return;
      }

      const result = await dispatch(
        startSession({
          presentationId: selectedPresentation.id,
          email,
          city,
        }),
      ).unwrap();

      // Result structure from PHP: { session_id: ..., event_session: { uuid: "...", ... } }
      // Our Slice return logic might unwrap 'data', so we check multiple places.
      // Priority: result.event_session.uuid (User's latest format) -> result.uuid -> fallback

      let newSessionUUID = result.event_session?.uuid;

      if (!newSessionUUID) {
        // Fallback: Check if it's at root or if we have to use numeric ID
        newSessionUUID = result.uuid || result.session_id || result.id;
      }

      if (!newSessionUUID) {
        setEmailError("Error starting session: No Session ID returned.");
        return;
      }

      // Keep using the UUID presentationId from params for the URL
      window.location.href = `/${uuid}/sessions/${newSessionUUID}`;
    } catch (error) {
      console.error("Failed to start session:", error);
      setEmailError("Failed to start session. " + error);
    } finally {
      setIsStarting(false);
    }
  };

  if (!sessionId) {
    return (
      <SpeakerRegistration
        email={email}
        setEmail={setEmail}
        emailError={emailError}
        setEmailError={setEmailError}
        city={city}
        setCity={setCity}
        cityError={cityError}
        setCityError={setCityError}
        handleStartSession={handleStartSession}
        isStarting={isStarting}
      />
    );
  }

  return (
    <div className="flex flex-col">
      <SpeakerNav
        openQRCode={openQRCode}
        setOpenQRCode={setOpenQRCode}
        handleEndPresentation={handleEndPresentation}
        getAttendeeUrl={getAttendeeUrl}
      />

      <div className="flex flex-col justify-center items-center">
        {slideData.length > 0 ? (
          <>
            {(slideData[currentSlide]?.type === "image" ||
              slideData[currentSlide]?.type === "pdf") && (
              <div className="relative h-[80vh] overflow-hidden">
                <img
                  src={slideData[currentSlide].imageUrl}
                  alt={slideData[currentSlide].title}
                  className="w-[65vw] h-full object-contain"
                />
                <SlideCanvasRenderer slide={slideData[currentSlide]} />
              </div>
            )}

            {slideData[currentSlide]?.type === "blank" && (
              <div className="relative h-[80vh] w-[65vw] overflow-hidden">
                <div
                  className="w-full h-full"
                  style={{
                    backgroundColor:
                      slideData[currentSlide].canvasBackgroundColor ||
                      "#1a1a2e",
                  }}
                />
                <SlideCanvasRenderer slide={slideData[currentSlide]} />
              </div>
            )}

            {(slideData[currentSlide].type === "text" ||
              slideData[currentSlide].type === "multiple" ||
              slideData[currentSlide].type === "poll") &&
              (() => {
                const slide = slideData[currentSlide];
                const isPoll = slide.type === "poll";
                const isMultiple = slide.type === "multiple";
                const hasCanvas =
                  (slide.animation_data?.elements || []).length > 0;

                const resultsControls = (isPoll || isMultiple) && (
                  <>
                    <button
                      onClick={() => setShowResults(true)}
                      className="absolute bottom-[4%] right-[2%] z-10 px-[1.5vw] py-[0.4vw] rounded-[0.5vw] bg-lightPurple text-white font-medium text-[1.2vw] hover:bg-purpleColor transition"
                    >
                      Show Results
                    </button>
                    {showResults && (
                      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                        <div
                          className="bg-white rounded-[0.5vw] w-[80%] max-w-[50vw] max-h-[90vh] overflow-y-auto relative"
                          style={{ scrollbarWidth: "thin" }}
                        >
                          <button
                            onClick={() => setShowResults(false)}
                            className="absolute top-3 right-2 text-gray-700 hover:text-gray-900 font-bold text-[1vw]"
                          >
                            <FontAwesomeIcon icon={faX} size="sm" />
                          </button>
                          <ResultsChart
                            slideData={slide}
                            results={getCurrentSlideResults()}
                            slideType={slide.type}
                          />
                        </div>
                      </div>
                    )}
                  </>
                );

                // Canvas layout — question/choices are positioned, animatable
                // elements rendered exactly like other slide types.
                if (hasCanvas) {
                  const bgColor =
                    slide.animation_data?.canvasBackgroundColor || "#f3f4f6";
                  const bgImage = slide.animation_data?.canvasBackground;

                  return (
                    <div
                      className="relative h-[80vh] w-[65vw] flex items-center justify-center overflow-hidden rounded-[0.5vw]"
                      style={{
                        backgroundColor: bgColor,
                        ...(bgImage
                          ? {
                              backgroundImage: `url("${bgImage}")`,
                              backgroundSize: "contain",
                              backgroundPosition: "center",
                              backgroundRepeat: "no-repeat"
                            }
                          : {}),
                      }}
                    >
                      <SlideCanvasRenderer slide={slide} />
                      {resultsControls}
                    </div>
                  );
                }

                // Fallback — question never opened in the editor (no canvas data).
                return (
                  <div className="relative w-[65vw] h-[calc(100vh-8vw)] rounded-[0.5vw] flex flex-col justify-center items-start p-5 md:p-8 md:pl-10 bg-gray-100">
                    <p className="text-gray-700 text-[3.5vw] lg:text-[2vw] font-medium">
                      {slide.question}
                    </p>
                    {slide.type === "text" && (
                      <textarea
                        readOnly
                        placeholder="Attendees answer here..."
                        className="mt-6 w-full p-3 border rounded-lg text-gray-400 bg-white/60"
                        rows={4}
                      />
                    )}
                    {(isPoll || isMultiple) && (
                      <div className="mt-5 space-y-3 w-full">
                        {slide.options?.map((choice, i) => (
                          <label
                            key={i}
                            className="flex items-center space-x-3 text-gray-700 text-[3vw] lg:text-[1.5vw]"
                          >
                            <span className="shrink-0 h-[3vw] w-[3vw] lg:h-[1.5vw] lg:w-[1.5vw] border border-gray-400 rounded-full" />
                            <span>
                              {typeof choice === "object"
                                ? choice.choice_text
                                : choice}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                    {resultsControls}
                  </div>
                );
              })()}
          </>
        ) : (
          <p>No slide Data available for this presentation.</p>
        )}

        {showEndConfirmModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-[100] animate-fadeIn backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-[90%] max-w-[400px] p-8 shadow-2xl transform transition-all scale-100 flex flex-col items-center text-center">
              <div className="w-20 h-20 bg-purple-50 text-purpleColor rounded-full flex items-center justify-center mb-6 text-3xl">
                <FontAwesomeIcon icon={faFolderClosed} />
              </div>

              <h2 className="text-2xl font-bold text-gray-800 mb-3">
                End Presentation?
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                This will terminate the session for all attendees. This action
                cannot be undone.
              </p>

              <div className="flex flex-col w-full gap-3">
                <button
                  onClick={confirmEndPresentation}
                  disabled={isEnding}
                  className="w-full py-4 bg-lightPurple text-white rounded-xl font-bold hover:bg-opacity-90 transition-all shadow-lg hover:shadow-purple-200 disabled:opacity-50"
                >
                  {isEnding ? "Ending..." : "Yes, End Presentation"}
                </button>
                <button
                  onClick={() => setShowEndConfirmModal(false)}
                  disabled={isEnding}
                  className="w-full py-4 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col items-center justify-center gap-6 border-t py-[0.5vw] px-[0.4vw] lg:flex-row lg:justify-between ">
        <button
          onClick={handlePreviousSlide}
          disabled={currentSlide <= 0}
          className="px-[0.8vw] py-[0.3vw] rounded-[0.5vw] lg:text-[1.5vw] bg-gray-200 text-gray-700 font-medium disabled:opacity-50 hover:bg-gray-300"
        >
          ← Previous
        </button>

        <div className="flex flex-col items-center w-full lg:w-auto lg:flex-1">
          <span className="text-gray-700 font-semibold text-[1.5vw] mb-[0.2vw]">
            {currentSlide + 1} / {slideData.length}
          </span>

          <div className="w-[70%] sm:w-[60%] md:w-[50%] bg-gray-200 rounded-full h-[0.5vw] overflow-hidden">
            <div
              className="bg-gradient-to-l from-lightPurple to-purpleColor h-[0.5vw] rounded-full transition-all duration-500 ease-in-out"
              style={{
                width: `${((currentSlide + 1) / slideData.length) * 100}%`,
              }}
            ></div>
          </div>
        </div>

        <button
          onClick={handleNextSlide}
          disabled={currentSlide >= slideData.length - 1}
          className="px-[0.8vw] py-[0.3vw] lg:text-[1.5vw] rounded-[0.5vw] bg-lightPurple text-white font-medium disabled:opacity-50 hover:bg-purpleColor transition-colors"
        >
          Next →
        </button>
      </div>
    </div>
  );
};
export default Speaker;
